import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useOrbit } from "../provider";
import { OrbitBillingPanel } from "./OrbitBillingPanel";
import { OrbitBillingSettingsPanel } from "./OrbitBillingSettingsPanel";
import { OrbitInboxPanel } from "./OrbitInboxPanel";
import { OrbitTaskBoard } from "./OrbitTaskBoard";
import { ORBIT_LOGO_SRC } from "../brand";
import { normalizeConnectionIssue, type OrbitClientPanelConnectionIssue } from "./connectionIssue";
import type { BridgeBillingNotice } from "@tuxx/orbit-bridge";

type OrbitClientPanelTab = "inbox" | "messages" | "status" | "requests" | "files" | "billing";
type OrbitClientPanelVisibleTab = "inbox" | "status" | "billing" | "settings";
type OrbitClientPanelLauncherPosition = "bottom-right" | "bottom-left" | "top-right" | "inline";
type OrbitClientPanelLauncherVariant = "label" | "icon";
type OrbitClientPanelConnectionStatus = "checking" | "connected" | "needs_connect" | "setup_required";

export interface OrbitClientPanelProps {
  /** Render inline as a panel or fixed as a bottom-right launcher. */
  mode?: "panel" | "floating";
  /** First tab to show when the panel opens. */
  defaultTab?: OrbitClientPanelTab;
  /** Label for the floating launcher. */
  launcherLabel?: string;
  /** Fixed placement for the floating launcher and panel. */
  launcherPosition?: OrbitClientPanelLauncherPosition;
  /** Use a compact Orbit mark instead of a text pill. */
  launcherVariant?: OrbitClientPanelLauncherVariant;
  /** Heading shown in the panel header. */
  title?: string;
  /** Optional helper copy in the panel header. */
  subtitle?: string;
  /** Heading shown before the host app has completed its backend handshake. */
  connectTitle?: string;
  /** Helper copy shown before the host app has completed its backend handshake. */
  connectDescription?: string;
  /** Label for the client-side setup button. */
  connectLabel?: string;
  /** Optional className for the outer panel. */
  className?: string;
}

const BASE_TABS: Array<{ id: Exclude<OrbitClientPanelVisibleTab, "settings" | "billing">; label: string }> = [
  { id: "inbox", label: "Inbox" },
  { id: "status", label: "Work" },
];

const normalizeTab = (tab: OrbitClientPanelTab): OrbitClientPanelVisibleTab => {
  if (tab === "status" || tab === "billing") return tab;
  return "inbox";
};

export function OrbitClientPanel({
  mode = "panel",
  defaultTab = "inbox",
  launcherLabel = "Support",
  launcherPosition = "bottom-right",
  launcherVariant = "label",
  title = "Orbit",
  subtitle = "",
  connectTitle = "Connect Orbit",
  connectDescription = "Use this from your signed-in dashboard to connect messages, work updates, requests, files, recordings, and billing notices.",
  connectLabel = "Connect Orbit",
  className = "",
}: OrbitClientPanelProps) {
  const orbit = useOrbit();
  const [open, setOpen] = useState(mode !== "floating");
  const [expanded, setExpanded] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingMinimized, setRecordingMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<OrbitClientPanelVisibleTab>(() => normalizeTab(defaultTab));
  const [connectionStatus, setConnectionStatus] =
    useState<OrbitClientPanelConnectionStatus>("checking");
  const [hasCheckedConnection, setHasCheckedConnection] = useState(false);
  const [connectionIssue, setConnectionIssue] = useState<OrbitClientPanelConnectionIssue | null>(null);
  const [billingNotices, setBillingNotices] = useState<BridgeBillingNotice[]>([]);
  const connected = connectionStatus === "connected";

  const checkConnection = useCallback(async () => {
    setConnectionStatus("checking");
    setConnectionIssue(null);
    try {
      await orbit.whoami();
      setConnectionStatus("connected");
    } catch (err) {
      const issue = normalizeConnectionIssue(err);
      setConnectionStatus(issue.kind === "setup_required" ? "setup_required" : "needs_connect");
      setConnectionIssue(issue);
    } finally {
      setHasCheckedConnection(true);
    }
  }, [orbit]);

  useEffect(() => {
    if (!open || hasCheckedConnection) return;
    void checkConnection();
  }, [checkConnection, hasCheckedConnection, open]);

  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await orbit.billing.list();
        if (!cancelled) setBillingNotices(response.notices ?? []);
      } catch {
        if (!cancelled) setBillingNotices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connected, orbit]);

  useEffect(() => {
    if (activeTab === "billing" && billingNotices.length === 0) {
      setActiveTab("inbox");
    }
  }, [activeTab, billingNotices.length]);

  const retryConnection = () => {
    void checkConnection();
  };

  const handleRecordingChange = useCallback((active: boolean) => {
    setRecordingActive(active);
    if (!active) setRecordingMinimized(false);
  }, []);

  const tabs: Array<{ id: Exclude<OrbitClientPanelVisibleTab, "settings">; label: string }> = [
    ...BASE_TABS,
    ...(billingNotices.length > 0
      ? [{ id: "billing" as const, label: `Billing ${billingNotices.length}` }]
      : []),
  ];

  const panel = (
    <section
      className={`orbit-embed orbit-client-panel ${mode === "floating" ? "orbit-client-panel--floating-panel" : ""} ${expanded ? "orbit-client-panel--expanded" : ""} ${recordingMinimized ? "orbit-client-panel--recording-minimized" : ""} ${className}`}
      aria-label={title}
    >
      <header className="orbit-client-panel__header">
        <div className="orbit-client-panel__heading">
          <div className="orbit-client-panel__brand-row">
            <span className="orbit-client-panel__brand-mark" aria-hidden>
              <img src={ORBIT_LOGO_SRC} alt="" />
            </span>
            <h3 className="orbit-client-panel__title">{title}</h3>
          </div>
          {subtitle ? <p className="orbit-client-panel__subtitle">{subtitle}</p> : null}
        </div>
        {connected || mode === "floating" ? (
          <div className="orbit-client-panel__header-actions">
            {connected && !recordingMinimized ? (
              <div
                className="orbit-client-panel__tabs orbit-client-panel__tabs--header"
                role="tablist"
                aria-label="Orbit client panel"
                style={{ "--orbit-tab-count": tabs.length } as CSSProperties}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className="orbit-client-panel__tab"
                    data-tab={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
            {connected ? (
              <button
                type="button"
                className="orbit-client-panel__icon-button"
                onClick={() => setActiveTab((tab) => tab === "settings" ? "inbox" : "settings")}
                aria-label="Account and payment settings"
                aria-pressed={activeTab === "settings"}
              >
                ⚙
              </button>
            ) : null}
            {mode === "floating" ? (
              <>
                <button
                  type="button"
                  className="orbit-client-panel__header-button"
                  onClick={() => setExpanded((value) => !value)}
                  aria-label={expanded ? "Collapse Orbit panel" : "Expand Orbit panel"}
                >
                  {expanded ? "Collapse" : "Expand"}
                </button>
                <button
                  type="button"
                  className="orbit-client-panel__close"
                  onClick={() => {
                    if (recordingActive) {
                      setRecordingMinimized(true);
                      setExpanded(false);
                      return;
                    }
                    setOpen(false);
                  }}
                  aria-label={recordingActive ? "Minimize Orbit panel while recording" : "Close Orbit panel"}
                >
                  x
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="orbit-client-panel__body">
        {!connected ? (
          <div className={`orbit-client-panel__connect orbit-client-panel__connect--${connectionStatus}`}>
            <span className="orbit-client-panel__connect-mark" aria-hidden>
              <img src={ORBIT_LOGO_SRC} alt="" />
            </span>
            <div>
              <h4 className="orbit-client-panel__connect-title">
                {connectionIssue?.title ?? connectTitle}
              </h4>
              <p className="orbit-client-panel__connect-copy">
                {connectionIssue?.description ?? connectDescription}
              </p>
            </div>
            {connectionStatus === "checking" ? (
              <div className="orbit-client-panel__connect-status">Checking connection...</div>
            ) : (
              <button type="button" className="orbit-button" onClick={retryConnection}>
                {connectionIssue?.actionLabel ?? connectLabel}
              </button>
            )}
            {connectionIssue?.detail ? (
              <div className="orbit-client-panel__connect-error">{connectionIssue.detail}</div>
            ) : null}
          </div>
        ) : null}
        {connected && activeTab === "inbox" ? (
          <OrbitInboxPanel
            recordingMinimized={recordingMinimized}
            onMinimizeForRecording={() => {
              setRecordingMinimized(true);
              setExpanded(false);
            }}
            onRestoreFromRecording={() => setRecordingMinimized(false)}
            onRecordingChange={handleRecordingChange}
          />
        ) : null}
        {connected && activeTab === "status" ? (
          <OrbitTaskBoard title="Project work" className="orbit-client-panel__work" />
        ) : null}
        {connected && activeTab === "billing" ? (
          <OrbitBillingPanel notices={billingNotices} />
        ) : null}
        {connected && activeTab === "settings" ? (
          <OrbitBillingSettingsPanel />
        ) : null}
      </div>
    </section>
  );

  if (mode !== "floating") return panel;

  const launcher = (
    <button
      type="button"
      className={`orbit-client-panel__launcher orbit-client-panel__launcher--${launcherVariant}`}
      onClick={() => setOpen(true)}
      aria-label={launcherLabel}
    >
      <span className="orbit-client-panel__launcher-mark" aria-hidden>
        <img src={ORBIT_LOGO_SRC} alt="" />
      </span>
      {launcherVariant === "icon" ? (
        <span className="orbit-client-panel__sr-only">{launcherLabel}</span>
      ) : (
        launcherLabel
      )}
    </button>
  );

  const floatingPanel = open ? (
    <div
      className={`orbit-client-panel__portal orbit-client-panel__portal--${launcherPosition} ${expanded ? "orbit-client-panel__portal--expanded" : ""}`}
    >
      {panel}
    </div>
  ) : null;

  return (
    <>
      <div className={`orbit-client-panel__floating orbit-client-panel__floating--${launcherPosition}`}>
        {!open ? launcher : null}
      </div>
      {floatingPanel && typeof document !== "undefined"
        ? createPortal(floatingPanel, document.body)
        : floatingPanel}
    </>
  );
}
